import { useRef, useState } from "react";
import { useI18n } from "../../../i18n";
import { Goal } from "./ScenePanels";
import { AiCameraPanel } from "./AiCameraPanel";
import { useCameraConsent } from "../api/cameraApi";
import type { AiVia } from "../hooks/useAiBridge";
import { QUESTS, questDone, questGoal, quizTotal, wantedCard } from "../lib/aiQuest";
import type { Quest, QuestState } from "../lib/aiQuest";
import type { RadioValue, partnerView } from "../lib/radio";

/** 서로 다른 카드(또는 카메라로 보인 것) 몇 가지로 보드 반응을 보면 목표 */
export const CARD_GOAL = 3;
/** 프로그램에 기다리는 이름이 없을 때 진열대 기본 카드(AI 차시 단골 — 과일 · 표정 · O/X) */
export const DEFAULT_CARDS = ["사과", "바나나", "웃음", "슬픔", "O", "X"];

/**
 * AI 카드 놀이(PICNIC-MB-FIELD-001 §4.7 · §6 3단계 · §7 결정 4) — 카드 · 물건을 보여 주는 두 길.
 *  ① 진짜 카메라: 보호자 동의(Campus — 제어 평면이 서버 간으로 묻는다 · 30초마다)가 ok 일 때만 카메라 패널을 그린다. 아이가 사진을 담아 학습시킨 모델이 고른 클래스가 보드로.
 *  ② 가상 카드: 진열대의 카드를 카메라 칸으로 끌어다 놓으면(또는 누르면) 그 이름이 바로 보드로 — 「AI 가 배운 것이 아니라 카드 이름을 바로 넣었어요」 표시로 구별.
 * 카드 이름은 아이 프로그램이 기다리는 이름(Bench 가 알려 준다)이 먼저, 없으면 기본 카드. 놀이터는 카드가 아니라 <b>카드에 반응한 보드</b>(왼쪽 칸 · 아래 '보드 반응')를 본다.
 */
/** 짝 화면(짝에게 놀이 — AI10 · 11 · 23 · 25 · 26 · 27 · 31 · 32) — 라디오 광장 기록에서: 내가 보낸 수 · 같은 그룹 짝 이름 · 짝에게서 온 것(최근) */
export type PartnerView = ReturnType<typeof partnerView>;

export function AiCardsPanel({ programClasses, send, boardLabel, su, quest, questState, cards: questCardsIn, onCard, onPickQuest, temp, partner, waking = null }: {
  programClasses: readonly string[];
  send: (klass: string, confidence: number, via: AiVia) => boolean;
  boardLabel: string;
  su: boolean;
  /** AI 차시 놀이(lib/aiQuest — §6.12) — 없으면 '자유 카드 놀이'(서로 다른 카드 3가지) */
  quest?: Quest | null;
  questState?: QuestState;
  /** 놀이에 쓰는 카드(아이 프로그램 이름 · 없으면 차시 스타터 이름) */
  cards?: readonly string[];
  onCard?: (card: string) => void;
  onPickQuest?: (id: string | null) => void;
  /** 보드 온도(시뮬레이터 칸 · 진짜 보드 센서 블록) — 추울 때만 놀이(AI29)가 보인다 */
  temp?: number;
  partner?: PartnerView;
  /** 보드를 아직 쓸 수 없다 — off: 놀이터에 보드가 없다(작품을 불러오지 않음) · waking: 막 왔다(보낸 카드를 시뮬레이터가 놓치면 아이는 프로그램 탓으로 안다) */
  waking?: "off" | "waking" | null;
}) {
  const { t } = useI18n();
  const showRadio = (v: RadioValue) => v.kind === "number" ? String(v.n) : v.kind === "string" ? `"${v.s}"` : v.kind === "value" ? `${v.name} = ${v.n}` : t("student.microbit.scene.plaza.other");
  const [tab, setTab] = useState<"card" | "camera">("card");
  const [dropped, setDropped] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [reactions, setReactions] = useState<{ card: string; board: string }[]>([]);
  const consent = useCameraConsent(tab === "camera");
  const cards = questCardsIn && questCardsIn.length ? questCardsIn : programClasses.length ? programClasses : DEFAULT_CARDS;

  const boardRef = useRef(boardLabel);
  boardRef.current = boardLabel;
  const lastCamera = useRef("");
  /** 보드가 반응할 틈(시뮬레이터 0.1초 화면 소식)을 두고 그때의 보드를 적는다 — 카드든 카메라든 */
  const note = (card: string) => window.setTimeout(() => setReactions((r) => [...r, { card, board: boardRef.current }].slice(-6)), 900);
  const put = (card: string) => {
    if (!send(card, 100, "card")) return;
    setDropped(card);
    note(card);
    onCard?.(card);
  };
  const fromCamera = (k: string, c: number) => {
    if (!k) { lastCamera.current = ""; return; }
    if (!send(k, c, "camera")) return;
    if (k !== lastCamera.current) { lastCamera.current = k; note(k); onCard?.(k); }   // 보이는 것이 바뀔 때만 적는다
  };

  const tried = new Set(reactions.map((r) => r.card)).size;
  // 프로그램이 기다리는 이름이 3가지보다 적으면 그 수만큼(사과 · 바나나 둘이면 둘 다) — 채울 수 없는 목표를 두지 않는다
  const goal = Math.max(1, Math.min(CARD_GOAL, cards.length));
  const qs = quest && questState ? questState : null;
  const qGoal = quest ? (quest.kind === "quiz" ? quest.goal : questGoal(quest, cards)) : 0;
  const qDone = !!(quest && qs && questDone(quest, cards, qs));
  const want = quest && qs ? wantedCard(quest, cards, qs) : null;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-aiCards">
      {onPickQuest ? (
        <label className="pf-mb__scenes">
          <span className="pf-mb__scenes-label">{t("student.microbit.ai.quest.pick")}</span>
          <select className="pf-mb__scene-select" value={quest?.id ?? ""} data-testid="fs-mb-ai-quest-pick" onChange={(e) => onPickQuest(e.target.value || null)}>
            <option value="">{t("student.microbit.ai.quest.free")}</option>
            {QUESTS.map((x) => <option key={x.id} value={x.id}>{x.id} · {t(`student.microbit.ai.quest.${x.id}.name`)}</option>)}
          </select>
        </label>
      ) : null}
      {quest && qs ? (
        <div className="pf-mb__ai-quest" data-testid="fs-mb-ai-quest" data-kind={quest.kind}>
          <p className="pf-mb__bubble" data-testid="fs-mb-ai-quest-ask">
            <span className="pf-mb__big">{quest.emoji}</span>{" "}
            {qDone ? t("student.microbit.ai.quest.done")
              : quest.kind === "quiz" ? t("student.microbit.ai.quest.ask.quiz", { q: t(`student.microbit.ai.quest.quizQ.${Math.min(qs.answered, quizTotal(quest) - 1)}`) })
              : t(`student.microbit.ai.quest.ask.${quest.kind}`, { card: want ?? "" })}
          </p>
          <p className="pf-mb__hint">{t(`student.microbit.ai.quest.${quest.id}.how`)}</p>
          {quest.cold ? (
            <p className="pf-mb__scene-state" data-testid="fs-mb-ai-quest-temp" data-cold={temp !== undefined && temp < quest.cold.below || undefined}>
              {temp === undefined ? t("student.microbit.ai.quest.coldUnknown") : t("student.microbit.ai.quest.cold", { t: temp, below: quest.cold.below })}
            </p>) : null}
          {qs.last ? <p className="pf-mb__scene-state" data-testid="fs-mb-ai-quest-last">{t(`student.microbit.ai.quest.last.${qs.last.result}`, { card: qs.last.card, below: quest.cold?.below ?? 0 })}</p> : null}
          {quest.kind === "radio" && partner ? (
            <div className="pf-mb__ai-partner" data-testid="fs-mb-ai-quest-partner">
              <p className="pf-mb__scene-state"><b>📡 {t("student.microbit.ai.quest.partner.title")}</b> · {t("student.microbit.ai.quest.partner.me", { n: partner.me })}
                {partner.names.length ? ` · ${t("student.microbit.ai.quest.partner.names", { names: partner.names.join(", ") })}` : ""}</p>
              {partner.got.length
                ? <ul className="pf-mb__ai-partner-got">{partner.got.map((g, i) => <li key={i} data-testid="fs-mb-ai-quest-partner-got">{t("student.microbit.ai.quest.partner.from", { name: g.name, v: showRadio(g.value) })}</li>)}</ul>
                : <p className="pf-mb__hint">{t("student.microbit.ai.quest.partner.none")}</p>}
            </div>) : null}
        </div>
      ) : null}
      <div className="pf-mb__ai-tabs" role="tablist" aria-label={t("student.microbit.ai.ways")}>
        <button type="button" role="tab" aria-selected={tab === "card"} onClick={() => setTab("card")} data-testid="fs-mb-ai-tab-card">🃏 {t("student.microbit.ai.tabCard")}</button>
        <button type="button" role="tab" aria-selected={tab === "camera"} onClick={() => setTab("camera")} data-testid="fs-mb-ai-tab-camera">📷 {t("student.microbit.ai.tabCamera")}</button>
      </div>

      {tab === "card" ? (
        <>
          {waking ? <p className="pf-mb__hint" role="status" data-testid="fs-mb-ai-waking">{waking === "off" ? `🔌 ${t("student.microbit.ai.noBoard")}` : `⏳ ${t("student.microbit.ai.waking")}`}</p> : null}
          <div className="pf-mb__ai-shelf" aria-label={t("student.microbit.ai.shelf")}>
            {cards.map((c) => (
              <button key={c} type="button" className="pf-mb__ai-card" draggable={!waking} disabled={!!waking} data-testid="fs-mb-ai-card"
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", c); e.dataTransfer.effectAllowed = "copy"; }}
                      onClick={() => put(c)}>
                🃏 {c}
              </button>
            ))}
          </div>
          <div className="pf-mb__ai-drop" data-over={over || undefined} data-testid="fs-mb-ai-drop"
               onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
               onDrop={(e) => { e.preventDefault(); setOver(false); const c = e.dataTransfer.getData("text/plain"); if (c && cards.includes(c) && !waking) put(c); }}>
            📷 {dropped ? t("student.microbit.ai.dropped", { card: dropped }) : t("student.microbit.ai.dropHere")}
          </div>
          <p className="pf-mb__ai-note" data-testid="fs-mb-ai-card-note">{t("student.microbit.ai.cardNote")}</p>
          {programClasses.length === 0 ? <p className="pf-mb__hint">{t("student.microbit.ai.noProgramClasses")}</p> : null}
        </>
      ) : consent === "ok" ? (
        <AiCameraPanel programClasses={programClasses} su={su} onClass={fromCamera} />
      ) : (
        <p className="pf-mb__bubble" data-testid="fs-mb-ai-consent" data-state={consent}>{t(`student.microbit.ai.consent.${consent}`)}</p>
      )}

      <p className="pf-mb__scene-state" data-testid="fs-mb-ai-board">{t("student.microbit.ai.board", { board: boardLabel || "—" })}</p>
      {reactions.length ? (
        <ol className="pf-mb__radio-log" data-testid="fs-mb-ai-reactions">
          {reactions.map((r, i) => <li key={i}>{t("student.microbit.ai.reaction", { card: r.card, board: r.board || "—" })}</li>)}
        </ol>
      ) : null}
      {quest && qs ? (
        <Goal id="fs-mb-scene-goal" done={qDone}
              text={t(`student.microbit.ai.quest.${quest.id}.goal`, { n: Math.min(qs.count, qGoal), goal: qGoal, total: quizTotal(quest), answered: qs.answered })} />
      ) : (
        <Goal id="fs-mb-scene-goal" done={tried >= goal} text={t("student.microbit.ai.goal", { goal })} />
      )}
    </div>
  );
}
